import {
  BodyText,
  DatePicker,
  Field,
  FieldError,
  FieldLabel,
} from "@/components/ui";
import { formatDate, formatUtcDateTime } from "@/lib/time";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { SubmitCancelButtons } from "./SubmitCancelButtons";
import type {
  GenericObservationDisplayProps,
  GenericObservationFormProps,
} from "./types";

const formSchema = z.object({
  date: z.date(),
});

export const GenericObservationDateForm = ({
  patientId,
  item,
  itemCategory,
  onSubmit,
  onCancel,
  isSubmitting,
}: GenericObservationFormProps) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      date: item.documentedObservation?.valueDateTime
        ? new Date(item.documentedObservation.valueDateTime)
        : undefined,
    },
  });
  const handleSubmit = (data: z.infer<typeof formSchema>) => {
    const fhirValue = {
      valueDateTime: formatUtcDateTime(data.date.toISOString()),
    };
    const payload = {
      patientId: patientId as string,
      category: itemCategory,
      status: "final",
      code: {
        coding: [
          {
            code: item.observationCode,
            system: item.observationCodeSystem,
            display: item.observationCodeDisplay,
          },
        ],
        text: item.observationCodeFriendlyDisplay,
      },
      fhirValue,
    };
    onSubmit(payload);
  };
  return (
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <div className="flex flex-col gap-2">
        <Controller
          control={form.control}
          name="date"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="date">
                <BodyText size="sm" bold>
                  {item.observationCodeFriendlyDisplay}
                </BodyText>
              </FieldLabel>
              <DatePicker
                {...field}
                autoFocus
                label="Date"
                date={field.value}
                setDate={field.onChange}
              />
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
        <SubmitCancelButtons isSaving={isSubmitting} onCancel={onCancel} />
      </div>
    </form>
  );
};

export const GenericObservationDateDisplay = ({
  item,
}: GenericObservationDisplayProps) => {
  return (
    <BodyText size="sm">
      {item.documentedObservation?.valueDateTime
        ? formatDate(item.documentedObservation.valueDateTime)
        : "Unknown"}
    </BodyText>
  );
};
